import { Position } from '@xyflow/react';
import { FLOATING_ICON_BOX_SIZE } from '@/utils/workflow';

/**
 * Shared configuration for the collapse "cluster" affordance — the toggle that
 * expands/collapses a container's children on the designer canvas.
 *
 * A *cluster* is any canvas node that fans out to one or more collapsible
 * children through a set of scoped branch handles:
 *   - `agent`   → tool nodes (handle `tools`)
 *   - `foreach` → loop body   (handle `loop`; `Until` reuses this kind)
 *   - `if`      → True/False branches (handles `true` / `false`)
 *   - `switch`  → case + default branches (handles `case-<name>` / `default`)
 *
 * Bare `Scope` is intentionally excluded in v1 (the workflow store's mutation
 * path does not yet traverse Scope children). The config is written to be
 * scope-ready so adding it later is a single-entry change.
 *
 * Both the expanded control (`ClusterEdge`) and the collapsed placeholder
 * (`ClusterStubNode`) read their noun + geometry from here so the two states
 * stay in lockstep across orientations.
 */
export type ClusterKind = 'agent' | 'if' | 'switch' | 'foreach';

/** All cluster kinds in canonical ordering. */
export const CLUSTER_KINDS: readonly ClusterKind[] = ['agent', 'if', 'switch', 'foreach'];

interface ClusterNoun {
  singular: string;
  plural: string;
}

/** Accessible noun for the children a given cluster kind hides. */
export const CLUSTER_NOUNS: Record<ClusterKind, ClusterNoun> = {
  agent: { singular: 'tool', plural: 'tools' },
  if: { singular: 'action', plural: 'actions' },
  switch: { singular: 'action', plural: 'actions' },
  foreach: { singular: 'action', plural: 'actions' },
};

/** Singular/plural noun pair for a cluster kind (falls back to item/items). */
export const getClusterNoun = (kind: ClusterKind): ClusterNoun =>
  CLUSTER_NOUNS[kind] ?? { singular: 'item', plural: 'items' };

/**
 * True for source handles that fan out to a container's *collapsible*
 * children. Deliberately excludes the "after"/continuation handles
 * (`merge`, `done`) that leave the scope and must never be collapsed.
 *
 * Keep in sync with the branch handle sets in `workflowHelpers.ts`:
 *   if -> true/false, switch -> case-<name>/default, foreach -> loop.
 */
export const isScopeBranchHandle = (handle: string | null | undefined): boolean =>
  handle === 'loop' ||
  handle === 'true' ||
  handle === 'false' ||
  handle === 'default' ||
  (typeof handle === 'string' && handle.startsWith('case-'));

/**
 * True for any source handle that fans out to a container's collapsible
 * children — the scope branch handles PLUS the agent `tools` handle. Used to
 * index a container's child edges so the cluster toggle can anchor at the
 * fan-out root regardless of container kind.
 */
export const isClusterChildHandle = (handle: string | null | undefined): boolean =>
  handle === 'tools' || isScopeBranchHandle(handle);

/** Nominal floating-node icon box; the fan-out handles sit on its edges. */
const NODE_ICON_HALF = FLOATING_ICON_BOX_SIZE / 2;

/**
 * The side of a container its collapsible children fan out from, per layout
 * orientation. Mirrors the *actual* source-handle positions declared in the
 * node components, so the collapsed anchor never crosses the node body:
 *   - agent   → `tools` handle is always Top (tools fan up), both orientations.
 *   - foreach → `loop`  handle: Top (horizontal) / Bottom (vertical).
 *   - if      → primary `true` handle: Top (horizontal) / Bottom (vertical).
 *   - switch  → case handles sit on the node `output` side: Right (horizontal)
 *               / Bottom (vertical). This is the one kind whose fan-out is NOT
 *               Top in horizontal layout.
 *
 * The container label always renders *below* the icon, so anchoring the
 * collapsed placeholder on these sides keeps it clear of the label.
 */
export const getFanoutPosition = (kind: ClusterKind, isHorizontal: boolean): Position => {
  switch (kind) {
    case 'agent':
      return Position.Top;
    case 'switch':
      return isHorizontal ? Position.Right : Position.Bottom;
    case 'if':
    case 'foreach':
    default:
      return isHorizontal ? Position.Top : Position.Bottom;
  }
};

/**
 * Canonical source handle the cluster toggle edge originates from, per kind.
 * Pinned so the collapsed dashed line always leaves from the same, correctly
 * positioned handle regardless of graph edge order (which is non-deterministic
 * and, for `if`, would otherwise pick the Bottom `false` handle in horizontal
 * layout and cross the node).
 *
 * `switch` is intentionally `undefined`: its case handle ids are dynamic
 * (`case-<name>` / `default`) but *all co-located* on the same `output` side,
 * so any actual child handle is geometrically equivalent — the caller falls
 * back to the container's first indexed child handle.
 */
export const PRIMARY_HANDLE_BY_KIND: Record<ClusterKind, string | undefined> = {
  agent: 'tools',
  foreach: 'loop',
  if: 'true',
  switch: undefined,
};

/** Where the collapsed anchor sits relative to the container, and how it faces back. */
export interface ClusterStubAnchor {
  /** Offset of the (invisible) anchor from the container node's top-left, in px. */
  offset: { x: number; y: number };
  /** Side of the anchor that faces the container (its target handle). */
  handlePosition: Position;
}

/**
 * Place the collapsed anchor a fixed distance *outward* from the container's
 * fan-out handle, and face its target handle back toward the container. The
 * anchor is invisible — the collapse chip itself rides the cluster edge at the
 * fan-out handle — so only the direction (not sub-pixel placement) matters.
 */
export const getStubAnchor = (
  fanoutPosition: Position,
  distance = 72,
): ClusterStubAnchor => {
  switch (fanoutPosition) {
    case Position.Bottom:
      return { offset: { x: NODE_ICON_HALF, y: FLOATING_ICON_BOX_SIZE + distance }, handlePosition: Position.Top };
    case Position.Left:
      return { offset: { x: -distance, y: NODE_ICON_HALF }, handlePosition: Position.Right };
    case Position.Right:
      return { offset: { x: FLOATING_ICON_BOX_SIZE + distance, y: NODE_ICON_HALF }, handlePosition: Position.Left };
    case Position.Top:
    default:
      return { offset: { x: NODE_ICON_HALF, y: -distance }, handlePosition: Position.Bottom };
  }
};

/** Id prefix for the derived collapsed-stub node (excluded from auto-layout). */
export const CLUSTER_STUB_PREFIX = 'cluster-stub-';

/** Build the canonical stub node id for a container node id. */
export const clusterStubId = (containerNodeId: string): string =>
  `${CLUSTER_STUB_PREFIX}${containerNodeId}`;

/** Build the canonical cluster-toggle edge id for a container node id. */
export const clusterEdgeId = (containerNodeId: string): string =>
  `cluster-toggle-${containerNodeId}`;
